/**
 * Le modèle de la bannière 88 × 31 — sans DOM, sans canvas.
 *
 * L'objet le plus reconnaissable du web de 1998 : le petit bouton clignotant
 * qu'on collait en bas de sa page perso, et qu'on échangeait avec les sites
 * voisins. Le format est figé depuis toujours, 88 sur 31, et c'est cette
 * fixité qui fait tout le charme : rien à mettre en page, tout à faire tenir.
 *
 * Ici vivent le nettoyage de la saisie, la découpe en deux lignes, les
 * palettes, et la conversion d'une image en index de palette. Le dessin
 * lui-même a besoin d'un canvas et reste dans le composant.
 */

import { S } from '../../i18n/strings';
import type { Leaf } from '../../i18n/strings';
import { deaccent } from '../../lib/text';

export const BANNER_WIDTH = 88;
export const BANNER_HEIGHT = 31;

/** Largeur de la vignette de gauche, celle qui porte le poisson. */
export const BANNER_BADGE_WIDTH = 24;

/** Longueur maximale du texte, et d'une de ses deux lignes. */
export const BANNER_MAX_LENGTH = 22;
export const BANNER_MAX_PER_LINE = 11;

export interface BannerScheme {
  readonly id: string;
  readonly label: Leaf;
  /** Fond de la zone de texte. */
  readonly bg: string;
  /** Couleur du texte au repos. */
  readonly ink: string;
  /** Couleur du texte sur la seconde image — c'est elle qui fait clignoter. */
  readonly accent: string;
  /** Filet du cadre. */
  readonly edge: string;
}

/**
 * Cinq combinaisons d'époque. Aucune n'est douce : ces boutons se voyaient de
 * loin sur des pages chargées, et un dégradé n'aurait de toute façon pas tenu
 * dans une palette de huit couleurs.
 */
export const BANNER_SCHEMES: readonly BannerScheme[] = [
  {
    id: 'nuit',
    label: S.y2k.banner.scheme_nuit,
    bg: '#000080',
    ink: '#ffffff',
    accent: '#ffff00',
    edge: '#c0c0c0',
  },
  {
    id: 'neon',
    label: S.y2k.banner.scheme_neon,
    bg: '#000000',
    ink: '#00ff00',
    accent: '#ff00ff',
    edge: '#00ffff',
  },
  {
    id: 'systeme',
    label: S.y2k.banner.scheme_systeme,
    bg: '#c0c0c0',
    ink: '#000000',
    accent: '#ff0000',
    edge: '#808080',
  },
  {
    id: 'bonbon',
    label: S.y2k.banner.scheme_bonbon,
    bg: '#ff00ff',
    ink: '#ffffff',
    accent: '#ffff00',
    edge: '#800080',
  },
  {
    id: 'aquarium',
    label: S.y2k.banner.scheme_aquarium,
    bg: '#003b57',
    ink: '#7fe3ff',
    accent: '#ffffff',
    edge: '#0a6f96',
  },
];

export interface BannerSpeed {
  readonly id: string;
  readonly label: Leaf;
  readonly delayMs: number;
}

export const BANNER_SPEEDS: readonly BannerSpeed[] = [
  { id: 'lent', label: S.y2k.banner.speed_slow, delayMs: 900 },
  { id: 'normal', label: S.y2k.banner.speed_normal, delayMs: 450 },
  { id: 'rapide', label: S.y2k.banner.speed_fast, delayMs: 180 },
];

export function findScheme(id: string): BannerScheme {
  return BANNER_SCHEMES.find((scheme) => scheme.id === id) ?? (BANNER_SCHEMES[0] as BannerScheme);
}

export function findSpeed(id: string): BannerSpeed {
  return BANNER_SPEEDS.find((speed) => speed.id === id) ?? (BANNER_SPEEDS[1] as BannerSpeed);
}

/**
 * La palette du fichier : huit couleurs, pas une de plus.
 *
 * Les quatre premières viennent du thème choisi, les quatre suivantes sont
 * fixes — le noir de la vignette, les deux oranges du poisson et le blanc de
 * son œil. **Steve n'emprunte jamais les couleurs du thème** : il est le seul
 * orange du site, et un poisson bleu sur une bannière bleue aurait été un
 * poisson bleu.
 *
 * Huit couleurs, c'est aussi ce qui donne au texte son bord net : la
 * quantification n'a nulle part où poser un pixel intermédiaire, donc le lissé
 * du canvas bascule d'un côté ou de l'autre. C'est exactement l'aspect
 * recherché.
 */
export function bannerPalette(scheme: BannerScheme): readonly string[] {
  return [scheme.bg, scheme.ink, scheme.accent, scheme.edge, '#000000', '#f97316', '#c2410c', '#ffffff'];
}

/** Index dans la palette ci-dessus, pour le dessin comme pour les tests. */
export const PALETTE_BG = 0;
export const PALETTE_INK = 1;
export const PALETTE_ACCENT = 2;
export const PALETTE_EDGE = 3;
export const PALETTE_BLACK = 4;
export const PALETTE_STEVE = 5;
export const PALETTE_STEVE_DARK = 6;
export const PALETTE_WHITE = 7;

/**
 * Ramène une saisie libre à ce qui peut tenir sur un bouton.
 *
 * Les accents sautent — ils passeraient, mais pas dans l'esprit d'un GIF de
 * 1998 — et les quatre caractères qui auraient une signification dans l'extrait
 * de code à copier (`<`, `>`, `"`, `&`) aussi : l'extrait est rendu dans un
 * `textarea`, donc rien ne s'exécute, mais un visiteur qui le colle vraiment
 * sur sa page mérite un attribut `alt` qui ne casse pas son HTML.
 */
export function sanitizeBannerText(raw: string): string {
  const flat = deaccent(raw)
    // Les caractères de contrôle sont décrits par leurs points de code : les
    // écrire tels quels rendrait la règle illisible dans un « git diff ».
    // eslint-disable-next-line no-control-regex -- c'est précisément la cible
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/[<>"&]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  return flat.slice(0, BANNER_MAX_LENGTH);
}

/**
 * Coupe le texte en deux lignes au plus, sur les espaces quand c'est possible.
 *
 * Un mot plus long qu'une ligne est tranché net plutôt que débordé : sur 88
 * pixels de large, il n'y a pas de place pour discuter.
 */
export function splitBannerLines(text: string, perLine = BANNER_MAX_PER_LINE): readonly string[] {
  const clean = text.trim();
  if (clean === '') return [];
  if (clean.length <= perLine) return [clean];

  const cut = clean.lastIndexOf(' ', perLine);
  if (cut > 0) {
    const rest = clean.slice(cut + 1).trim();
    return [clean.slice(0, cut), rest.slice(0, perLine)];
  }
  return [clean.slice(0, perLine), clean.slice(perLine, perLine * 2)];
}

/** Le nom du fichier proposé au téléchargement. */
export const BANNER_FILENAME = 'poisson-steve-88x31.gif';

/**
 * L'extrait à recopier sur sa page.
 *
 * `border="0"` n'a plus aucun sens depuis vingt ans, et c'est bien pour ça
 * qu'il est là : sans lui, un navigateur de l'époque cernait l'image d'un cadre
 * bleu de lien, et toutes les pages du monde portaient cet attribut.
 */
export function bannerSnippet(text: string, href: string): string {
  const alt = text === '' ? 'LE POISSON STEVE' : text;
  return [
    `<a href="${href}">`,
    `<img src="${BANNER_FILENAME}" width="${String(BANNER_WIDTH)}" height="${String(BANNER_HEIGHT)}"`,
    ` alt="${alt}" border="0">`,
    '</a>',
  ].join('');
}

/**
 * Convertit des pixels RVBA en index de palette, au plus proche voisin.
 *
 * La distance est calculée sans racine carrée : comparer des carrés suffit à
 * les classer, et l'appel est fait une fois par pixel.
 */
export function quantise(
  pixels: Uint8ClampedArray | Uint8Array,
  palette: readonly string[],
): Uint8Array {
  const table = palette.map((color) => {
    const value = Number.parseInt(color.slice(1), 16);
    return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff] as const;
  });

  const count = Math.floor(pixels.length / 4);
  const out = new Uint8Array(count);
  for (let index = 0; index < count; index += 1) {
    const r = pixels[index * 4] ?? 0;
    const g = pixels[index * 4 + 1] ?? 0;
    const b = pixels[index * 4 + 2] ?? 0;
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const [entry, [pr, pg, pb]] of table.entries()) {
      const distance = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = entry;
      }
    }
    out[index] = best;
  }
  return out;
}
